import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from './CloseIcon';
import './RemoveSellReasonModal.scss';

const REMOVAL_REASONS = [
  'SOLD_VIA_JAAKKO',
  'SOLD_ELSEWHERE',
  'NO_LONGER_SELLING',
  'JUST_TESTING_INTEREST',
  'NOT_ENOUGH_CONTACTS',
  'PREFER_NOT_TO_SAY',
  'OTHER',
] as const;

interface RemoveSellReasonModalProps {
  isOpen: boolean;
  isRemoving: boolean;
  onClose: () => void;
  onConfirm: (reason: string, reasonText?: string) => void;
}

const RemoveSellReasonModal: React.FC<RemoveSellReasonModalProps> = ({
  isOpen,
  isRemoving,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedReason(null);
      setOtherText('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isOther = selectedReason === 'OTHER';
  const canConfirm = selectedReason && (!isOther || otherText.trim().length > 0);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(selectedReason!, isOther ? otherText.trim() : undefined);
  };

  return ReactDOM.createPortal(
    <div
      className="end-chat-confirmation-overlay remove-sell-reason-modal-overlay"
      onClick={() => !isRemoving && onClose()}
    >
      <div
        className="end-chat-confirmation remove-sell-reason-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-sell-reason-title"
        aria-describedby="remove-sell-reason-subtitle"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="end-chat-confirmation__header">
          <h3 id="remove-sell-reason-title">
            {t('building.offMarket.modal.removeSell.title')}
          </h3>
          <button
            className="end-chat-confirmation__close"
            onClick={onClose}
            disabled={isRemoving}
            aria-label={t('building.offMarket.modal.removeSell.closeLabel')}
          >
            <CloseIcon color="#202125" size={16} />
          </button>
        </div>

        <div className="end-chat-confirmation__content">
          <p
            id="remove-sell-reason-subtitle"
            className="remove-sell-reason-modal__subtitle"
          >
            {t('building.offMarket.modal.removeSell.subtitle')}
          </p>

          <div className="remove-sell-reason-modal__reasons">
            {REMOVAL_REASONS.map((reason) => (
              <label
                key={reason}
                className={`remove-sell-reason-modal__reason${
                  selectedReason === reason
                    ? ' remove-sell-reason-modal__reason--selected'
                    : ''
                }`}
              >
                <input
                  type="radio"
                  name="removal-reason"
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={() => setSelectedReason(reason)}
                  className="remove-sell-reason-modal__radio"
                />
                <span className="remove-sell-reason-modal__reason-text">
                  {t(`building.offMarket.modal.removeSell.reasons.${reason}`)}
                </span>
              </label>
            ))}
          </div>

          {isOther && (
            <textarea
              className="remove-sell-reason-modal__other-input"
              placeholder={t('building.offMarket.modal.removeSell.otherPlaceholder')}
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              rows={3}
              maxLength={500}
              autoFocus
            />
          )}
        </div>

        <div className="end-chat-confirmation__actions">
          <button
            className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
            onClick={onClose}
            disabled={isRemoving}
          >
            {t('building.offMarket.modal.removeSell.cancelButton')}
          </button>
          <button
            className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
            onClick={handleConfirm}
            disabled={!canConfirm || isRemoving}
          >
            {isRemoving
              ? t('building.offMarket.modal.loading', 'Loading...')
              : t('building.offMarket.modal.removeSell.confirmButton')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RemoveSellReasonModal;
